const numericFields = [
  "units",
  "unitSizeSF",
  "rentLow",
  "rentHigh",
  "netRent",
  "lowSF",
  "highSF",
  "occupancyPercent",
  "averageOccupancyPercent",
];

export const generateId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "." || trimmed === "-" || trimmed === "-.") return null;

  const normalized = trimmed.replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const toTextOrEmpty = (value) => (value === null || value === undefined ? "" : String(value));

const toIsoOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/** Trim, drop empties, and dedupe tags case-insensitively (first spelling wins). */
export const normalizeTags = (value) => {
  const raw = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];
  const seen = new Set();
  const tags = [];
  raw.forEach((tag) => {
    const text = toTextOrEmpty(tag).trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    tags.push(text);
  });
  return tags;
};

export const createBlankNote = (timestamp = new Date().toISOString()) => ({
  id: generateId(),
  text: "",
  tags: [],
  createdAt: timestamp,
  updatedAt: timestamp,
});

/**
 * Normalize a note payload. Accepts the new `{ id, text, createdAt, updatedAt }`
 * shape and the legacy plain-string shape (converted to a single note stamped
 * with `fallbackTimestamp`).
 */
export const normalizeNote = (note, fallbackTimestamp = null) => {
  const fallback = toIsoOrNull(fallbackTimestamp) || new Date().toISOString();

  if (typeof note === "string") {
    return { id: generateId(), text: note, tags: [], createdAt: fallback, updatedAt: fallback };
  }

  const createdAt = toIsoOrNull(note?.createdAt) || toIsoOrNull(note?.updatedAt) || fallback;
  const updatedAt = toIsoOrNull(note?.updatedAt) || createdAt;

  return {
    id: note?.id || generateId(),
    text: toTextOrEmpty(note?.text),
    tags: normalizeTags(note?.tags),
    createdAt,
    updatedAt,
  };
};

export const normalizeNotes = (value, fallbackTimestamp = null) => {
  if (value === null || value === undefined) return [];

  if (typeof value === "string") {
    return value.trim() ? [normalizeNote(value, fallbackTimestamp)] : [];
  }

  if (!Array.isArray(value)) return [];

  return value
    .filter((note) => note !== null && note !== undefined)
    .map((note) => normalizeNote(note, fallbackTimestamp))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

export const sanitizeNotesForStorage = (notes = []) =>
  normalizeNotes(notes).filter((note) => note.text.trim().length > 0);

/** Every distinct tag used by any note across the given entries, sorted A-Z. */
export const collectNoteTags = (entries = []) => {
  const seen = new Map();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    normalizeNotes(entry?.notes, entry?.surveyDate).forEach((note) => {
      note.tags.forEach((tag) => {
        const key = tag.toLowerCase();
        if (!seen.has(key)) seen.set(key, tag);
      });
    });
  });
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
};

export const isNoteFilterActive = (filter) =>
  Boolean(toTextOrEmpty(filter?.search).trim()) || normalizeTags(filter?.tags).length > 0;

/**
 * Search matches note text or any tag (case-insensitive substring); tag filter
 * requires every selected tag. Notes with no text yet (fresh drafts) always
 * match so a note added while a filter is active stays visible.
 */
export const noteMatchesFilter = (note, filter) => {
  if (!isNoteFilterActive(filter)) return true;
  const text = toTextOrEmpty(note?.text);
  if (!text.trim()) return true;

  const tags = normalizeTags(note?.tags).map((tag) => tag.toLowerCase());
  const search = toTextOrEmpty(filter?.search).trim().toLowerCase();
  if (search && !text.toLowerCase().includes(search) && !tags.some((tag) => tag.includes(search))) {
    return false;
  }

  const wanted = normalizeTags(filter?.tags).map((tag) => tag.toLowerCase());
  return wanted.every((tag) => tags.includes(tag));
};

export const entryMatchesNoteFilter = (entry, filter) =>
  !isNoteFilterActive(filter)
  || normalizeNotes(entry?.notes, entry?.surveyDate).some(
    (note) => toTextOrEmpty(note.text).trim() && noteMatchesFilter(note, filter)
  );

export const hasNoteText = (notes = []) =>
  (Array.isArray(notes) ? notes : []).some((note) => toTextOrEmpty(note?.text).trim().length > 0);

export const calculateRentPerSF = (rentValue, unitSizeValue) => {
  const rent = toNumberOrNull(rentValue);
  const unitSize = toNumberOrNull(unitSizeValue);

  if (rent === null || unitSize === null || unitSize <= 0) {
    return null;
  }

  return Number((rent / unitSize).toFixed(2));
};

export const applyCalculatedRowFields = (row = {}) => {
  const normalizedRow = {
    id: row?.id || generateId(),
    units: toNumberOrNull(row?.units),
    unitType: toTextOrEmpty(row?.unitType),
    unitSizeSF: toNumberOrNull(row?.unitSizeSF),
    rentLow: toNumberOrNull(row?.rentLow),
    rentHigh: toNumberOrNull(row?.rentHigh),
    netRent: toNumberOrNull(row?.netRent),
    lowSF: toNumberOrNull(row?.lowSF),
    highSF: toNumberOrNull(row?.highSF),
  };

  normalizedRow.lowSF = calculateRentPerSF(normalizedRow.rentLow, normalizedRow.unitSizeSF);
  normalizedRow.highSF = calculateRentPerSF(normalizedRow.rentHigh, normalizedRow.unitSizeSF);

  return normalizedRow;
};

export const createBlankRow = () => ({
  id: generateId(),
  units: null,
  unitType: "",
  unitSizeSF: null,
  rentLow: null,
  rentHigh: null,
  netRent: null,
  lowSF: null,
  highSF: null,
});

export const createBlankEntry = () => ({
  id: generateId(),
  surveyDate: null,
  occupancyPercent: null,
  averageOccupancyPercent: null,
  notes: [],
  rows: [createBlankRow()],
});

export const hasRowContent = (row = {}) => {
  if (!row || typeof row !== "object") return false;
  return [
    row.units,
    row.unitType,
    row.unitSizeSF,
    row.rentLow,
    row.rentHigh,
    row.netRent,
    row.lowSF,
    row.highSF,
  ].some((value) => {
    if (typeof value === "string") return value.trim().length > 0;
    return value !== null && value !== undefined;
  });
};

export const ensureTrailingEditableRow = (rows = []) => {
  const safeRows = Array.isArray(rows)
    ? rows.map((row) => applyCalculatedRowFields({ ...createBlankRow(), ...row }))
    : [createBlankRow()];

  if (safeRows.length === 0) {
    return [createBlankRow()];
  }

  while (
    safeRows.length > 1
    && !hasRowContent(safeRows[safeRows.length - 1])
    && !hasRowContent(safeRows[safeRows.length - 2])
  ) {
    safeRows.pop();
  }

  if (hasRowContent(safeRows[safeRows.length - 1])) {
    safeRows.push(createBlankRow());
  }

  return safeRows;
};

export const sanitizeRowsForStorage = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => applyCalculatedRowFields(row))
    .filter((row) => hasRowContent(row));

export const calculateOccupancyMetrics = (rows = []) => {
  const safeRows = Array.isArray(rows) ? rows : [];

  let totalUnits = 0;
  let occupiedUnits = 0;
  const rowOccupancies = [];

  safeRows.forEach((row) => {
    if (!hasRowContent(row)) return;

    const units = toNumberOrNull(row?.units);
    if (units === null || units <= 0) return;

    const hasRentInput = [
      toNumberOrNull(row?.rentLow),
      toNumberOrNull(row?.rentHigh),
      toNumberOrNull(row?.netRent),
    ].some((value) => value !== null);

    totalUnits += units;
    if (hasRentInput) {
      occupiedUnits += units;
    }

    rowOccupancies.push(hasRentInput ? 100 : 0);
  });

  const occupancyPercent = totalUnits > 0 ? Number(((occupiedUnits / totalUnits) * 100).toFixed(2)) : null;
  const averageOccupancyPercent =
    rowOccupancies.length > 0
      ? Number((rowOccupancies.reduce((sum, value) => sum + value, 0) / rowOccupancies.length).toFixed(2))
      : null;

  return {
    occupancyPercent,
    averageOccupancyPercent,
  };
};

export const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return number.toLocaleString("en-US", options);
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
};

export const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return `${number.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
};

export const formatDateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDateTimeLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const computeEntrySummary = (entry) => {
  const rows = Array.isArray(entry?.rows) ? entry.rows.map(applyCalculatedRowFields) : [];
  let totalSF = 0;
  let totalUnits = 0;
  let totalRent = 0;
  let rentCount = 0;
  let totalUnitSize = 0;
  let totalUnitSizeCount = 0;

  const unitMix = new Set();

  rows.forEach((row) => {
    if (!hasRowContent(row)) return;

    const units = toNumberOrNull(row.units);
    const unitSize = toNumberOrNull(row.unitSizeSF);

    if (row.unitType && String(row.unitType).trim()) {
      unitMix.add(String(row.unitType).trim());
    }

    if (units !== null) {
      totalUnits += units;
    }

    if (unitSize !== null) {
      totalUnitSize += unitSize;
      totalUnitSizeCount += 1;
    }

    if (units !== null && unitSize !== null) {
      totalSF += units * unitSize;
    }

    const avgRentCandidate = [toNumberOrNull(row.netRent), toNumberOrNull(row.rentLow), toNumberOrNull(row.rentHigh)]
      .find((value) => value !== null);

    if (avgRentCandidate !== undefined && avgRentCandidate !== null) {
      totalRent += avgRentCandidate;
      rentCount += 1;
    }
  });

  const occupancyMetrics = calculateOccupancyMetrics(rows);

  return {
    totalSF,
    totalUnits,
    averageUnitSize: totalUnitSizeCount > 0 ? totalUnitSize / totalUnitSizeCount : null,
    averageRent: rentCount > 0 ? totalRent / rentCount : null,
    averageRentPerSF: totalSF > 0 && totalRent > 0 ? totalRent / totalSF : null,
    unitMixLabel: unitMix.size > 0 ? Array.from(unitMix).join(" / ") : "Unit Mix",
    occupancyPercent: occupancyMetrics.occupancyPercent,
    averageOccupancyPercent: occupancyMetrics.averageOccupancyPercent,
  };
};

export const normalizeEntry = (entry) => {
  const normalizedRows = ensureTrailingEditableRow(
    (Array.isArray(entry?.rows) && entry.rows.length > 0 ? entry.rows : [createBlankRow()]).map((row) =>
      applyCalculatedRowFields({
        id: row?.id || generateId(),
        units: row?.units,
        unitType: row?.unitType,
        unitSizeSF: row?.unitSizeSF,
        rentLow: row?.rentLow,
        rentHigh: row?.rentHigh,
        netRent: row?.netRent,
        lowSF: row?.lowSF,
        highSF: row?.highSF,
      })
    )
  );

  const occupancy = calculateOccupancyMetrics(normalizedRows);

  return {
    id: entry?.id || generateId(),
    surveyDate: entry?.surveyDate || null,
    occupancyPercent: occupancy.occupancyPercent,
    averageOccupancyPercent: occupancy.averageOccupancyPercent,
    notes: normalizeNotes(entry?.notes, entry?.surveyDate),
    rows: normalizedRows,
  };
};

export { numericFields };
